import { Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { SprintDetail, SprintGoal } from '@/services/sprint.service';

export interface TaskFilters {
  search: string;
  phase_status: string;
  priority: string;
  assignee_id: string;
  column_id: string;
  goal_id: string;
}

const emptyFilters: TaskFilters = {
  search: '', phase_status: '', priority: '', assignee_id: '', column_id: '', goal_id: '',
};

interface SprintTaskFiltersProps {
  filters: TaskFilters;
  onChange: (filters: TaskFilters) => void;
  members: SprintDetail['members'];
  columns: SprintDetail['columns'];
  goals: SprintGoal[];
}

export function SprintTaskFilters({ filters, onChange, members, columns, goals }: SprintTaskFiltersProps) {
  const hasActiveFilters = filters.phase_status || filters.priority || filters.assignee_id || filters.column_id || filters.goal_id;

  function update(partial: Partial<TaskFilters>) {
    onChange({ ...filters, ...partial });
  }

  return (
    <div className="space-y-3">
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search tasks by title, description, or reference..."
          value={filters.search}
          onChange={(e) => update({ search: e.target.value })}
          className="pl-9"
        />
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <Select value={filters.phase_status || 'all'} onValueChange={(v) => update({ phase_status: v === 'all' ? '' : v })}>
          <SelectTrigger className="w-[140px] h-8 text-xs"><SelectValue placeholder="All Phases" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Phases</SelectItem>
            <SelectItem value="not_started">Not Started</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="done">Done</SelectItem>
          </SelectContent>
        </Select>

        <Select value={filters.priority || 'all'} onValueChange={(v) => update({ priority: v === 'all' ? '' : v })}>
          <SelectTrigger className="w-[140px] h-8 text-xs"><SelectValue placeholder="All Priorities" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Priorities</SelectItem>
            <SelectItem value="low">Low</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="critical">Critical</SelectItem>
          </SelectContent>
        </Select>

        <Select value={filters.assignee_id || 'all'} onValueChange={(v) => update({ assignee_id: v === 'all' ? '' : v })}>
          <SelectTrigger className="w-[160px] h-8 text-xs"><SelectValue placeholder="All Assignees" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Assignees</SelectItem>
            {members.map((m) => (
              <SelectItem key={m.user?.id || m.id} value={m.user?.id || m.id}>
                {m.user?.full_name || 'Unknown'}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={filters.goal_id || 'all'} onValueChange={(v) => update({ goal_id: v === 'all' ? '' : v })}>
          <SelectTrigger className="w-[160px] h-8 text-xs"><SelectValue placeholder="All Goals" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Goals</SelectItem>
            {goals.map((g) => (
              <SelectItem key={g.id} value={g.id}>{g.title}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={filters.column_id || 'all'} onValueChange={(v) => update({ column_id: v === 'all' ? '' : v })}>
          <SelectTrigger className="w-[140px] h-8 text-xs"><SelectValue placeholder="All Stages" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Stages</SelectItem>
            {columns.map((c) => (
              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {hasActiveFilters && (
          <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => onChange({ ...emptyFilters, search: filters.search })}>
            <X className="mr-1 h-3 w-3" /> Clear
          </Button>
        )}
      </div>
    </div>
  );
}

export { emptyFilters };
